import React, { useState } from 'react';
import { TaskCategory, CategoryFormData, DeadlineRule } from '../types';
import SidePanel from '../../../UI/SidePanel';
import BasicInfoTab from './CategoryFormTabs/BasicInfoTab';
import AppearanceTab from './CategoryFormTabs/AppearanceTab';
import SchemesTab from './CategoryFormTabs/SchemesTab';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import styles from '../../../../styles/PointScheme.module.css';

interface AddEditCategoryPanelProps {
  isOpen: boolean;
  editingCategory: TaskCategory | null;
  formData: CategoryFormData;
  deadlineRules: DeadlineRule[];
  isUpdating: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onInputChange: (field: string, value: any) => void;
  onColorChange: (color: string) => void;
  onIconSelect: (iconName: string) => void;
  onAddRule: () => void;
  onUpdateRule: (ruleId: string, field: string, value: any) => void;
  onRemoveRule: (ruleId: string) => void;
  isGroupSpecific?: boolean;
}

const AddEditCategoryPanel: React.FC<AddEditCategoryPanelProps> = ({
  isOpen,
  editingCategory,
  formData,
  deadlineRules,
  isUpdating,
  onClose,
  onSubmit,
  onInputChange,
  onColorChange,
  onIconSelect,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  isGroupSpecific = false
}) => {
  const [activeTab, setActiveTab] = useState('basic');

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={editingCategory 
        ? (isGroupSpecific ? 'Edit Group Point Scheme' : 'Edit Global Point Scheme')
        : (isGroupSpecific ? 'Add Group Point Scheme' : 'Add Global Point Scheme')
      }
      icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"></rect>
          <rect x="14" y="3" width="7" height="7"></rect>
          <rect x="14" y="14" width="7" height="7"></rect>
          <rect x="3" y="14" width="7" height="7"></rect>
        </svg>
      }
    >
      <div className={sidePanelStyles.sideBarTabs}>
        <div 
          className={`${sidePanelStyles.sideBarTab} ${activeTab === 'basic' ? sidePanelStyles.active : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </div>
        <div 
          className={`${sidePanelStyles.sideBarTab} ${activeTab === 'appearance' ? sidePanelStyles.active : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          Appearance
        </div>
        <div 
          className={`${sidePanelStyles.sideBarTab} ${activeTab === 'schemes' ? sidePanelStyles.active : ''}`}
          onClick={() => setActiveTab('schemes')}
        >
          Point Schemes
        </div>
      </div>
      
      <form className={sidePanelStyles.form} onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}>
        {activeTab === 'basic' && (
          <BasicInfoTab 
            formData={formData}
            onInputChange={onInputChange}
          />
        )}

        {activeTab === 'appearance' && (
          <AppearanceTab 
            formData={formData}
            onInputChange={onInputChange}
            onColorChange={onColorChange}
            onIconSelect={onIconSelect}
          />
        )}

        {activeTab === 'schemes' && (
          <SchemesTab 
            formData={formData}
            deadlineRules={deadlineRules}
            onInputChange={onInputChange}
            onAddRule={onAddRule}
            onUpdateRule={onUpdateRule}
            onRemoveRule={onRemoveRule}
          />
        )}

        <div className={sidePanelStyles.formActions}>
          <button 
            type="button"
            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
            onClick={onClose}
            disabled={isUpdating}
          >
            Cancel
          </button>
          <button 
            type="submit"
            className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
            disabled={!formData.name || !formData.description || isUpdating}
          >
            {isUpdating ? (
              <>
                <svg
                  className={styles.spinner}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                {editingCategory ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              editingCategory ? 'Update Scheme' : 'Create Scheme'
            )}
          </button>
        </div>
      </form>
    </SidePanel>
  );
};

export default AddEditCategoryPanel;

